import React from 'react'
import {useDispatch} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Card} from 'antd'

import Sankey from './Sankey'
import EmptyComponent from '../../../Components/Empty'
import AlertsCreators, {INITIAL_STATE as ALERTS_INITIAL_STATE} from '../../AlertsPage/reducer'
import {alertStatusColorMap} from '../../AlertsPage/utils/alertStatusMapping'
import {serviceMap} from '../../../Utils/utils'

// serviceMap covers the three alert networks; PayPal is a valid alert_service too, so
// give it a display name rather than leaking the raw enum value.
const SERVICE_LABELS = {...serviceMap, paypal: 'PayPal'}

// Blue gradient ramp across the left of the flow: the root and the networks are
// dimensions, not results, so they stay neutral blue and let the outcome column carry
// the meaning. Endpoints are on-palette and theme-keyed so they stay legible on the
// white and dark cards.
const RAMP = {
  light: ['#072F94', '#225AEA'], // blueDeep -> bluePrimary
  dark: ['#1448CB', '#225AEA'], // blueDepressed -> bluePrimary
}

// Two statuses sit on top of the card they are drawn on: #440F0E is invisible against
// the dark card (1.2:1) and #D0D0D4 against the white one (1.5:1). Step each to its
// neighbouring palette value, in the theme where it fails only, so the bar and its
// ribbons stay readable without drifting off the status hue.
const STATUS_COLOR_BY_THEME = {
  light: {Duplicate: '#A1A1AA'}, // Neutral400
  dark: {'Already a chargeback': '#9E1414'}, // redDepressed
}

// Outcome nodes reuse the status colours the alert status tag and the status filter
// already paint, so a green bar here means the same thing as a green tag in the table.
const statusColor = (status, dark) =>
  STATUS_COLOR_BY_THEME[dark ? 'dark' : 'light'][status] ||
  alertStatusColorMap[status] ||
  alertStatusColorMap['default']

const lerpHex = (a, b, t) => {
  const ca = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16))
  const cb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16))
  const mix = ca.map((v, i) => Math.round(v + (cb[i] - v) * t))
  return `#${mix.map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

const nodeLabel = (node) => (node.kind === 'service' ? SERVICE_LABELS[node.value] ?? node.value : node.value)

// Column index per node kind: the flow always reads all -> source -> outcome.
const COLUMN = {all: 0, service: 1, status: 2}
const LAST_COLUMN = 2

// Map the { nodes:[{id,kind,value}], links:[{source:id,target:id,value}] } flow onto the
// Sankey contract: nodes { name, color, ... }, links indexed into nodes. Outcome nodes
// take their status colour; the columns before them are sampled from the theme ramp,
// so each ribbon blends the neutral blue of a network into the colour of the outcome
// it lands on.
const toChartData = (flow, t, ramp, dark) => {
  const nodes = flow?.nodes || []
  const links = flow?.links || []
  const indexOf = new Map(nodes.map((n, i) => [n.id, i]))
  return {
    nodes: nodes.map((n) => ({
      name: t(nodeLabel(n)),
      color:
        n.kind === 'status'
          ? statusColor(n.value, dark)
          : lerpHex(ramp[0], ramp[1], (COLUMN[n.kind] ?? 0) / LAST_COLUMN),
      segment: n.id,
      kind: n.kind,
      raw: n.value,
      volume: n.volume,
    })),
    links: links
      .filter((l) => indexOf.has(l.source) && indexOf.has(l.target))
      .map((l) => ({
        source: indexOf.get(l.source),
        target: indexOf.get(l.target),
        value: l.value,
        volume: l.volume,
      })),
  }
}

// "Alert flow" section: how alerts from each network resolve. Clicking a bar or a
// ribbon pre-filters the Alerts table to exactly that slice and navigates there.
const SankeySection = ({flow, theme, period}) => {
  const {t} = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const dark = theme === 'dark'
  const hasData = !!flow && Array.isArray(flow.nodes) && flow.nodes.length > 0
  const chartData = hasData ? toChartData(flow, t, dark ? RAMP.dark : RAMP.light, dark) : null

  // Drill-in: pre-filter the existing alerts list to the clicked slice and navigate
  // there, reusing the same Redux filters the table already reads (no new route).
  const drillTo = (preset) => {
    dispatch(AlertsCreators.clearAlertsFilters())
    dispatch(
      AlertsCreators.alertsSetProp('filters', {
        ...ALERTS_INITIAL_STATE.filters,
        // Carry the dashboard's period across so the table matches what the Sankey drew.
        ...(period?.start_date && period?.end_date
          ? {alert_timestamp: [period.start_date, period.end_date]}
          : {}),
        ...preset,
      })
    )
    navigate('/alerts')
  }

  // A node drills to its own dimension; the root drills to the unfiltered table.
  const drillToNode = (node) => {
    if (node.kind === 'all') return drillTo({})
    return drillTo(node.kind === 'service' ? {alert_service: [node.raw]} : {alert_status: [node.raw]})
  }

  // A ribbon is a specific slice. Root -> source ribbons only pin the source; a
  // source -> outcome ribbon pins both, which is exactly the datapoint clicked.
  const drillToLink = (source, target) => {
    if (source.kind === 'all') return drillTo({alert_service: [target.raw]})
    return drillTo({alert_service: [source.raw], alert_status: [target.raw]})
  }

  return (
    <Card
      title={t('Alert flow')}
      className="card-chart-wrapper withTitle"
      styles={{header: {border: 'none'}}}
    >
      {hasData ? (
        <Sankey
          data={chartData}
          theme={theme}
          unit={t('alerts')}
          onNodeClick={drillToNode}
          onLinkClick={drillToLink}
        />
      ) : (
        <EmptyComponent type="no_data_available" />
      )}
    </Card>
  )
}

export default SankeySection
